import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import jQuery from 'jquery';
import { registerLicense } from '@syncfusion/ej2-base';
import AuthProvider from '@auth/AuthProvider';
import AuthGuard from '@auth/AuthGuard';
import { initWindowGlobals } from '@auth/initGlobals';
import InovxLoader from '@shared/global/InovxLoader'; // must be before any app code
import { getFrontendConfig } from './config/frontendConfig';
import AppHeader from '@shared/layout/AppHeader';
import Loading from '@shared/components/controls/Loading';
import AdminLayout from '@shared/layout/AdminLayout';
import HomeApp from '@admin/home/HomeApp';
import FacilityApp from '@facility/components/FacilityApp';
import ErrorPage from '@pages/ErrorPage';
import NotFoundPage from '@pages/NotFoundPage';
import NoAccessPage from '@pages/NoAccessPage';
import NoLicensePage from '@pages/NoLicensePage';
import LicenseAppPage from '@admin/license/LicenseAppPage';
import OrganizationAppPage from '@admin/organization/OrganizationAppPage';
import RoleAppPage from '@admin/role/RoleAppPage';
import EntitlementAppPage from '@admin/entitlement/EntitlementAppPage';
import UserAppPage from '@admin/user/UserAppPage';
import DiagnosticsAppPage from '@admin/diagnostics/DiagnosticsAppPage';
// Third-party CSS (order matters — base frameworks first, then overrides)
import 'bootstrap/dist/css/bootstrap.min.css';
import 'font-awesome/css/font-awesome.min.css';
import 'leaflet/dist/leaflet.css';
import '@syncfusion/ej2-base/styles/fabric.css';
import '@syncfusion/ej2-buttons/styles/fabric.css';
import '@syncfusion/ej2-icons/styles/fabric.css';
import '@syncfusion/ej2-inputs/styles/fabric.css';
import '@syncfusion/ej2-popups/styles/fabric.css';
import '@syncfusion/ej2-react-dropdowns/styles/fabric.css';
import '@syncfusion/ej2-react-grids/styles/fabric.css';
import '@syncfusion/ej2-react-inplace-editor/styles/fabric.css';
import '@syncfusion/ej2-react-layouts/styles/fabric.css';
import '@syncfusion/ej2-react-lists/styles/fabric.css';
import '@syncfusion/ej2-react-navigations/styles/fabric.css';
import '@syncfusion/ej2-react-popups/styles/fabric.css';
import '@syncfusion/ej2-react-treegrid/styles/fabric.css';
import '@syncfusion/ej2-querybuilder/styles/fabric.css';
import '@syncfusion/ej2-richtexteditor/styles/fabric.css';
import '@syncfusion/ej2-splitbuttons/styles/fabric.css';
import '@syncfusion/ej2-calendars/styles/fabric.css';

// Application styles (LESS — loaded after third-party so app styles can override)
import '@styles/main.less';

// Initialize window globals (replaces server-side injection from _SharedLayout.cshtml)
initWindowGlobals();

// Legacy jQuery global expected by existing codebase
(window as any).$ = jQuery;
(window as any).jQuery = jQuery;

// Syncfusion license
registerLicense('Ngo9BigBOggjGyl/VkR+XU9Ff1RDX3xKf0x/TGpQb19xflBPallYVBYiSV9jS3hTdUVhWHpedXZdQGFdVk91WQ==');

const frontendConfig = getFrontendConfig();

// Initialize the global loader with locale data. Before a platform session exists,
// the browser locale controls the sign-in shell language.
InovxLoader.setLocaleData(
  frontendConfig.locale.initialLocale,
    frontendConfig.locale.datePattern,
    frontendConfig.locale.timePattern,
    frontendConfig.locale.timezone
);
const loaderInitialization = InovxLoader.initialize();

// Legacy globals required by existing code
(window as any).DisplayMode = {
    Navigation: 'nav', CreateAnnotation: 'ann', PositionAnnotation: 'pos',
    CreateAssembly: 'asm', Measurement: 'mea', TagVolumeMove: 'tvlm',
    TagVolumeScale: 'tvls', AssemblyVolumeMove: 'avlm', AssemblyVolumeScale: 'avls',
};
(window as any).AnnotationType = {
    AssetTag: 'ast', AssetTagWithBox: 'asb', ObservationTag: 'obs',
    InformationAnnotation: 'inf', LinkAnnotation: 'lnk',
};
(window as any).MeasurementType = {
    Point: 'Point', XOrtho: 'XOrtho', YOrtho: 'YOrtho', ZOrtho: 'ZOrtho', Distance: 'Distance',
};
(window as any).AssetSourceType = { VPlant: 1, DocX: 2, Vnf: 3 };
(window as any).AttributeDataType = { String: 1, Date: 2, Link: 3, Boolean: 4, Number: 5 };
(window as any).TaxonomyType = { KB: 1, AssetSet: 2 };
(window as any).AssetSetType = { List: 1, All3DAssets: 2, Query: 3, AllAssetTags: 4 };

const basePath = frontendConfig.urls.basePath;

const StartupAppShellFallback: React.FC = () => (
  <>
    <AppHeader />
    <div className="auth-bootstrap-shell" aria-live="polite" aria-busy="true">
      <Loading />
    </div>
  </>
);

const root = createRoot(document.getElementById('app')!);

// index.html carries an English <title> so the tab has a name before the locale
// resources load; replace it once the active language is available.
const applyLocalizedDocumentTitle = () => {
  document.title = InovxLoader.t('common:app.title');
};

loaderInitialization.then(() => {
  applyLocalizedDocumentTitle();
  InovxLoader.subscribe(applyLocalizedDocumentTitle);
  root.render(
    <AuthProvider>
      <AuthGuard loadingFallback={<StartupAppShellFallback />}>
        <AppHeader />
        <BrowserRouter basename={basePath}>
          <Switch>
            <Redirect exact from="/" to="/home" />
            <Route path="/home" render={() => <AdminLayout><HomeApp /></AdminLayout>} />
            <Route path="/license" render={() => <AdminLayout><LicenseAppPage /></AdminLayout>} />
            <Route path="/organization" render={() => <AdminLayout><OrganizationAppPage /></AdminLayout>} />
            <Route path="/role" render={() => <AdminLayout><RoleAppPage /></AdminLayout>} />
            <Route path="/entitlement" render={() => <AdminLayout><EntitlementAppPage /></AdminLayout>} />
            <Route path="/user" render={() => <AdminLayout><UserAppPage /></AdminLayout>} />
            <Route path="/diagnostics" render={() => <AdminLayout><DiagnosticsAppPage /></AdminLayout>} />
            <Route path="/manage" render={() => <FacilityApp mode="manage" />} />
            <Route path="/error" component={ErrorPage} />
            <Route path="/not-found" component={NotFoundPage} />
            <Route path="/no-access" component={NoAccessPage} />
            <Route path="/no-license" component={NoLicensePage} />
            <Route render={() => <FacilityApp mode="explore" />} />
          </Switch>
        </BrowserRouter>
      </AuthGuard>
    </AuthProvider>
  );
});
